import { useState, useEffect } from 'react';
import { MdFolder as FolderIcon, MdMoreVert as MenuIcon, MdEdit as EditIcon, MdDelete as DeleteIcon, MdDriveFileMove as MoveIcon, MdSettings as SettingsIcon } from 'react-icons/md';
import { BsCheck2Circle as TaskIcon } from 'react-icons/bs';
import { ReactComponent as EpicIcon } from '@Assets/epicIcon.svg';
import { ReactComponent as StoryIcon } from '@Assets/storyIcon.svg';
import { ReactComponent as StageIcon } from '@Assets/stageIcon.svg';
import { ReactComponent as MilestoneIcon } from '@Assets/milestone.svg';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import EditFolderForm from '@ProjectFunctionalDisplay/EditFolderForm';
import DeleteProjectFolderForm from '@ProjectFunctionalDisplay/DeleteProjectFolderForm';
import MigrateFolderForm from '@ProjectFunctionalDisplay/MigrateFolderForm';
import ProjectFilePermission from '@ProjectFunctionalDisplay/ProjectFilePermission';
import './ProjectFolder.scss';

export default function ProjectFolder(props) {

    const [permission, setPermission] = useState(null);
    const [showInfoOptions, setShowInfoOptions] = useState(false);
    const [showEditFolderForm, setShowEditFolderForm] = useState(false);
    const [showDeleteFolderForm, setShowDeleteFolderForm] = useState(false);
    const [showFolderMigrateForm, setShowFolderMigrateForm] = useState(false);
    const [showFolderPermission, setShowFolderPermission] = useState(false);
    const [anchorInfoMenu, setAnchorInfoMenu] = useState(null);
    const className = 'projectFolder';

    useEffect(() => {
        if(!!props.folder) {
            setPermission(props.folder?.permission);
        }
    }, [props.folder])

    const openOptionsPopup = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (props.folderType !== 'projectFiles') return;
        setAnchorInfoMenu(event.currentTarget);
        setShowInfoOptions(true);
    }

    const closeOptionsPopup = (event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        setAnchorInfoMenu(null);
        setShowInfoOptions(false);
    }

    const toggleShowEditFolderForm = () => {
        setShowEditFolderForm(!showEditFolderForm);
        closeOptionsPopup();
    }

    const toggleShowDeleteFolderForm = () => {
        setShowDeleteFolderForm(!showDeleteFolderForm);
        closeOptionsPopup();
    }

    const toggleShowMigrateFolderForm = () => {
        setShowFolderMigrateForm(!showFolderMigrateForm);
        closeOptionsPopup();
    }

    const toggleShowFolderPermission = () => {
        setShowFolderPermission(!showFolderPermission);
        closeOptionsPopup();
    }

    const getFolderIcon = () => {
        switch(props?.folderType) {
            case 'task':
                return <TaskIcon className={`${className}__icon task`} />;
            case 'story':
                return <StoryIcon className={`${className}__icon story`} />;
            case 'epic':
                return <EpicIcon className={`${className}__icon epic`} />;
            case 'stage':
                return <StageIcon className={`${className}__icon stage`} />;
            case 'milestone':
                return <MilestoneIcon className={`${className}__icon milestone`} />;
            default:
                return <FolderIcon className={`${className}__icon default`} />;
        }
    }

    return (
        <>
            <div 
                className={className}
                onDoubleClick={props.handleFolderClick}
                onContextMenu={openOptionsPopup}>
                <div className={`${className}__content`}>
                    {props.folderType === 'projectFiles' && (
                        <button
                            type="button"
                            className={`${className}__menu`}
                            onClick={openOptionsPopup}
                            aria-label="Folder options"
                        >
                            <MenuIcon />
                        </button>
                    )}
                    <div className={`${className}__iconWrapper`}>
                        {getFolderIcon()}
                    </div>
                    <div className={`${className}__info`}>
                        <div className={`${className}__name`}>{props?.folderName}</div>
                    </div>
                </div>
            </div>

            {showInfoOptions && (
                <Menu
                    open={showInfoOptions}
                    onClose={closeOptionsPopup}
                    anchorEl={anchorInfoMenu}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    PaperProps={{
                        className: `${className}__contextMenu`,
                        sx: {
                            borderRadius: '10px',
                            border: '1px solid var(--color-border)',
                            background: 'var(--color-componentBackground)',
                            boxShadow: '0 10px 28px rgba(15, 23, 42, 0.25)',
                            minWidth: '190px',
                        }
                    }}
                    MenuListProps={{
                        className: `${className}__contextMenuList`
                    }}>
                    <MenuItem 
                        onClick={toggleShowEditFolderForm}
                        disabled={!permission?.allowEdit}>
                        <ListItemIcon>
                            <EditIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Rename</ListItemText>
                    </MenuItem>
                    <MenuItem 
                        onClick={toggleShowMigrateFolderForm}
                        disabled={true}>
                        <ListItemIcon>
                            <MoveIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Move</ListItemText>
                    </MenuItem>
                    <Divider />
                    <MenuItem 
                        onClick={toggleShowFolderPermission}
                        disabled={!permission?.allowAdmin}>
                        <ListItemIcon>
                            <SettingsIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Share</ListItemText>
                    </MenuItem>
                    <MenuItem 
                        onClick={toggleShowDeleteFolderForm}
                        disabled={!permission?.allowDelete}
                        sx={{ color: '#f44336' }}>
                        <ListItemIcon>
                            <DeleteIcon fontSize="small" sx={{ color: '#f44336' }} />
                        </ListItemIcon>
                        <ListItemText>Delete</ListItemText>
                    </MenuItem>
                </Menu>
            )}

            {showEditFolderForm && (
                <EditFolderForm
                    folderName={props.folderName}
                    folderId={props.folderId}
                    open={showEditFolderForm}
                    renderData={props.renderData}
                    navigationParentId={props.navigationParentId}
                    onClose={toggleShowEditFolderForm}/>
            )}

            {showDeleteFolderForm && (
                <DeleteProjectFolderForm
                    folderId={props.folderId}
                    folderName={props.folderName}
                    open={showDeleteFolderForm}
                    renderData={props.renderData}
                    navigationParentId={props.navigationParentId}
                    closePopup={toggleShowDeleteFolderForm}/>
            )}

            {showFolderMigrateForm && (
                <MigrateFolderForm
                    folderId={props.folderId}
                    open={showFolderMigrateForm}
                    renderData={props.renderData}
                    navigationParentId={props.navigationParentId}
                    closePopup={toggleShowMigrateFolderForm}/>
            )}

            {showFolderPermission && (
                <ProjectFilePermission
                    folderId={props.folderId}
                    permission={permission}
                    childType={'projectFolder'}
                    open={showFolderPermission}
                    renderData={props.renderData}
                    navigationParentId={props.navigationParentId}
                    closePopup={toggleShowFolderPermission}/>
            )}
        </>
    );
}
